// Dedicated dashboard for ad-feed ingest health (Google Shopping + Meta AIA/Retail).
// Shows at-a-glance KPIs per feed — last refresh status, HTTP code, response
// time, item count, success-rate sparkline, and the latest error — and
// embeds the full history table so admins have everything in one view.
import { createFileRoute, Link } from "@tanstack/react-router";
import { AdminOnlyGuard } from "@/components/admin/AdminOnlyGuard";
import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import {
  Activity,
  AlertTriangle,
  CheckCircle2,
  Clock,
  ExternalLink,
  Gauge,
  Hash,
  ListChecks,
  Rss,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { FeedRefreshHistoryCard } from "@/components/admin/FeedRefreshHistoryCard";
import {
  listFeedRefreshLogs,
  getFeedRefreshSummary,
  type FeedRefreshLog,
} from "@/lib/feed-refresh.functions";

export const Route = createFileRoute("/admin/feed-health")({
  head: () => ({
    meta: [
      { title: "Feed-sundhed — AutoXChange admin" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: () => (<AdminOnlyGuard><AdminFeedHealth /></AdminOnlyGuard>),
});

const FEED_LABELS: Record<string, string> = {
  google: "Google Shopping",
  facebook: "Facebook AIA",
  "facebook-retail": "Facebook Retail",
};

const FEED_ORDER = ["google", "facebook", "facebook-retail"] as const;

function formatRelative(iso: string | null): string {
  if (!iso) return "aldrig";
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.round(diff / 60_000);
  if (mins < 1) return "lige nu";
  if (mins < 60) return `${mins} min siden`;
  const hours = Math.round(mins / 60);
  if (hours < 48) return `${hours} t siden`;
  const days = Math.round(hours / 24);
  return `${days} dage siden`;
}

function formatDuration(ms: number | null | undefined): string {
  if (ms === null || ms === undefined) return "—";
  if (ms < 1000) return `${ms} ms`;
  return `${(ms / 1000).toFixed(2)} s`;
}

function StatusPill({ status }: { status: string | null }) {
  if (status === "ok") {
    return (
      <Badge className="border-transparent bg-emerald-500/15 text-emerald-700 hover:bg-emerald-500/15 dark:text-emerald-400">
        <CheckCircle2 className="mr-1 h-3 w-3" /> OK
      </Badge>
    );
  }
  if (status === "warning") {
    return (
      <Badge className="border-transparent bg-amber-500/15 text-amber-700 hover:bg-amber-500/15 dark:text-amber-400">
        <AlertTriangle className="mr-1 h-3 w-3" /> Advarsel
      </Badge>
    );
  }
  if (status === "error") {
    return (
      <Badge variant="destructive">
        <AlertTriangle className="mr-1 h-3 w-3" /> Fejl
      </Badge>
    );
  }
  return (
    <Badge variant="outline" className="text-muted-foreground">
      Ukendt
    </Badge>
  );
}

interface FeedKpi {
  feedName: string;
  label: string;
  lastStatus: string | null;
  lastRunAt: string | null;
  lastSuccessAt: string | null;
  lastHttpStatus: number | null;
  lastDurationMs: number | null;
  lastItemCount: number | null;
  lastError: string | null;
  recent: FeedRefreshLog[];
  successRate24h: number | null;
  runs24h: number;
}

function buildKpis(
  logs: FeedRefreshLog[],
  summary: { feed_name: string; last_success_at: string | null; last_success_item_count: number | null }[],
): FeedKpi[] {
  const summaryByFeed = new Map(summary.map((s) => [s.feed_name, s]));
  const logsByFeed = new Map<string, FeedRefreshLog[]>();
  for (const log of logs) {
    const arr = logsByFeed.get(log.feed_name) ?? [];
    arr.push(log);
    logsByFeed.set(log.feed_name, arr);
  }

  const dayAgo = Date.now() - 24 * 60 * 60 * 1000;

  return FEED_ORDER.map((feedName) => {
    const feedLogs = logsByFeed.get(feedName) ?? [];
    const last = feedLogs[0] ?? null;
    const summaryRow = summaryByFeed.get(feedName);
    const recent24h = feedLogs.filter(
      (l) => new Date(l.created_at).getTime() >= dayAgo,
    );
    const okCount24h = recent24h.filter((l) => l.status === "ok").length;
    const successRate24h =
      recent24h.length > 0 ? Math.round((okCount24h / recent24h.length) * 100) : null;

    return {
      feedName,
      label: FEED_LABELS[feedName] ?? feedName,
      lastStatus: last?.status ?? null,
      lastRunAt: last?.created_at ?? null,
      lastSuccessAt: summaryRow?.last_success_at ?? null,
      lastHttpStatus: last?.http_status ?? null,
      lastDurationMs: last?.duration_ms ?? null,
      lastItemCount: last?.item_count ?? summaryRow?.last_success_item_count ?? null,
      lastError: last?.error_message ?? null,
      recent: feedLogs.slice(0, 10),
      successRate24h,
      runs24h: recent24h.length,
    };
  });
}

function AdminFeedHealth() {
  const fetchLogs = useServerFn(listFeedRefreshLogs);
  const fetchSummary = useServerFn(getFeedRefreshSummary);

  const summaryQuery = useQuery({
    queryKey: ["feed-health-summary"],
    queryFn: () => fetchSummary(),
  });
  const logsQuery = useQuery({
    queryKey: ["feed-health-logs"],
    queryFn: () => fetchLogs(),
  });

  const kpis = useMemo(
    () =>
      buildKpis(
        logsQuery.data?.logs ?? [],
        summaryQuery.data?.summary ?? [],
      ),
    [logsQuery.data, summaryQuery.data],
  );

  const overallHealthy = kpis.every((k) => k.lastStatus === "ok" || k.lastStatus === null);

  return (
    <div className="space-y-6 p-6">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-semibold text-foreground">
            <Activity className="h-6 w-6 text-accent" />
            Feed-sundhed
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Live overblik over Google Shopping og Facebook feed-ingest. Status,
            HTTP-kode, responstid og item-antal opdateres efter hver
            cron-kørsel.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Badge
            variant="outline"
            className={
              overallHealthy
                ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-400"
                : "border-destructive/30 bg-destructive/10 text-destructive"
            }
          >
            {overallHealthy ? (
              <>
                <CheckCircle2 className="mr-1 h-3 w-3" /> Alle feeds sunde
              </>
            ) : (
              <>
                <AlertTriangle className="mr-1 h-3 w-3" /> Et eller flere feeds fejler
              </>
            )}
          </Badge>
          <Button asChild size="sm" variant="outline">
            <Link to="/admin/feeds">
              <Rss className="mr-2 h-3.5 w-3.5" /> Feed-indstillinger
            </Link>
          </Button>
        </div>
      </header>

      {/* KPI cards per feed */}
      <div className="grid gap-4 lg:grid-cols-3">
        {kpis.map((k) => (
          <FeedKpiCard key={k.feedName} kpi={k} />
        ))}
      </div>

      {/* Full history reuses existing card */}
      <FeedRefreshHistoryCard />
    </div>
  );
}

function FeedKpiCard({ kpi }: { kpi: FeedKpi }) {
  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between space-y-0 pb-3">
        <div>
          <CardTitle className="flex items-center gap-2 text-base">
            <Rss className="h-4 w-4 text-accent" />
            {kpi.label}
          </CardTitle>
          <p className="mt-1 text-xs text-muted-foreground">
            Sidst kørt {formatRelative(kpi.lastRunAt)}
          </p>
        </div>
        <StatusPill status={kpi.lastStatus} />
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <KpiTile
            icon={<ExternalLink className="h-3.5 w-3.5" />}
            label="HTTP"
            value={kpi.lastHttpStatus !== null ? String(kpi.lastHttpStatus) : "—"}
            tone={
              kpi.lastHttpStatus && kpi.lastHttpStatus >= 400
                ? "danger"
                : kpi.lastHttpStatus === 200
                  ? "ok"
                  : "neutral"
            }
          />
          <KpiTile
            icon={<Clock className="h-3.5 w-3.5" />}
            label="Responstid"
            value={formatDuration(kpi.lastDurationMs)}
            tone={
              kpi.lastDurationMs && kpi.lastDurationMs > 10_000
                ? "warn"
                : kpi.lastDurationMs && kpi.lastDurationMs > 0
                  ? "ok"
                  : "neutral"
            }
          />
          <KpiTile
            icon={<Hash className="h-3.5 w-3.5" />}
            label="Items"
            value={kpi.lastItemCount !== null ? kpi.lastItemCount.toLocaleString("da-DK") : "—"}
            tone={kpi.lastItemCount && kpi.lastItemCount > 0 ? "ok" : "neutral"}
          />
          <KpiTile
            icon={<Gauge className="h-3.5 w-3.5" />}
            label="Success 24t"
            value={
              kpi.successRate24h !== null
                ? `${kpi.successRate24h}%`
                : "—"
            }
            tone={
              kpi.successRate24h === null
                ? "neutral"
                : kpi.successRate24h >= 90
                  ? "ok"
                  : kpi.successRate24h >= 60
                    ? "warn"
                    : "danger"
            }
          />
        </div>

        {/* Tiny sparkline of last 10 runs */}
        <div>
          <div className="mb-1.5 flex items-center justify-between text-[11px] text-muted-foreground">
            <span className="flex items-center gap-1">
              <ListChecks className="h-3 w-3" /> Seneste {kpi.recent.length || 0} kørsler
            </span>
            <span>{kpi.runs24h} kørsler i 24t</span>
          </div>
          <div className="flex h-6 items-end gap-0.5">
            {kpi.recent.length === 0 && (
              <span className="text-[11px] italic text-muted-foreground">
                Ingen kørsler logget endnu
              </span>
            )}
            {[...kpi.recent].reverse().map((run) => (
              <span
                key={run.id}
                title={`${new Date(run.created_at).toLocaleString("da-DK")} · ${run.status}${
                  run.http_status ? ` · HTTP ${run.http_status}` : ""
                }${run.duration_ms ? ` · ${run.duration_ms}ms` : ""}`}
                className={`flex-1 rounded-sm ${
                  run.status === "ok"
                    ? "bg-emerald-500/70 h-full"
                    : run.status === "warning"
                      ? "bg-amber-500/70 h-2/3"
                      : "bg-destructive h-1/2"
                }`}
              />
            ))}
          </div>
        </div>

        {kpi.lastError && (
          <div className="rounded-md border border-destructive/30 bg-destructive/5 p-2.5">
            <p className="text-[11px] font-medium uppercase tracking-wide text-destructive">
              Sidste fejl
            </p>
            <p className="mt-1 break-words text-xs text-destructive">{kpi.lastError}</p>
          </div>
        )}

        {kpi.lastSuccessAt && kpi.lastStatus !== "ok" && (
          <p className="text-[11px] text-muted-foreground">
            Sidst godkendt: <strong>{formatRelative(kpi.lastSuccessAt)}</strong>
          </p>
        )}
      </CardContent>
    </Card>
  );
}

function KpiTile({
  icon,
  label,
  value,
  tone,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  tone: "ok" | "warn" | "danger" | "neutral";
}) {
  const toneClass =
    tone === "ok"
      ? "text-emerald-700 dark:text-emerald-400"
      : tone === "warn"
        ? "text-amber-700 dark:text-amber-400"
        : tone === "danger"
          ? "text-destructive"
          : "text-foreground";
  return (
    <div className="rounded-lg border border-border bg-muted/40 p-3">
      <p className="flex items-center gap-1 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
        {icon}
        {label}
      </p>
      <p className={`mt-1 text-lg font-semibold ${toneClass}`}>{value}</p>
    </div>
  );
}
